import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { DARK_SCHEME_QUERY, appearanceCookieDomain, applyAppearance, readStoredAppearance, resolveAppearance, storeAppearance, } from './preference.js';
/**
 * Parent domains this page tried to write the cookie on and the browser refused (a public suffix it knows): the
 * choice then lives in a host-only cookie, so it is not shared. Module state, as the cookie jar it mirrors is.
 */
const refusedDomains = new Set();
/** Fired on window after this page stores a new preference, so every hook instance on the page follows. */
const CHANGE_EVENT = 'trace:appearance';
function subscribePreference(notify) {
    // storage: another tab of this origin changed it. focus and visibilitychange: the cookie may have been changed by
    // the other app (the hub while Trace Admin sat in a background tab); cookies fire no event, so look again on return.
    const onVisibility = () => {
        if (document.visibilityState === 'visible')
            notify();
    };
    window.addEventListener(CHANGE_EVENT, notify);
    window.addEventListener('storage', notify);
    window.addEventListener('focus', notify);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
        window.removeEventListener(CHANGE_EVENT, notify);
        window.removeEventListener('storage', notify);
        window.removeEventListener('focus', notify);
        document.removeEventListener('visibilitychange', onVisibility);
    };
}
function subscribeSystem(notify) {
    if (typeof window.matchMedia !== 'function')
        return () => { };
    const media = window.matchMedia(DARK_SCHEME_QUERY);
    media.addEventListener('change', notify);
    return () => media.removeEventListener('change', notify);
}
function systemDark() {
    return typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_QUERY).matches;
}
/**
 * The appearance preference as React state, applied to the document root. While the preference is System the page
 * follows the device's scheme live. `hubOrigin` names the shared parent domain the choice is stored for (see
 * appearanceCookieDomain); pass the app's configured hub origin.
 */
export function useAppearance(hubOrigin) {
    const preference = useSyncExternalStore(subscribePreference, () => readStoredAppearance(), () => 'system');
    const dark = useSyncExternalStore(subscribeSystem, systemDark, () => false);
    const resolved = resolveAppearance(preference, dark);
    useEffect(() => {
        applyAppearance(resolved);
    }, [resolved]);
    const domain = appearanceCookieDomain(window.location.hostname, hubOrigin);
    const shared = domain !== null && !refusedDomains.has(domain);
    const setPreference = useCallback((next) => {
        const written = storeAppearance(next, hubOrigin);
        const wanted = appearanceCookieDomain(window.location.hostname, hubOrigin);
        if (wanted && written === null)
            refusedDomains.add(wanted);
        window.dispatchEvent(new Event(CHANGE_EVENT));
    }, [hubOrigin]);
    return { preference, resolved, shared, setPreference };
}
/** The label people read for each choice, in the order the controls offer them. */
export const APPEARANCE_LABELS = {
    system: 'System',
    light: 'Light',
    dark: 'Dark',
};
//# sourceMappingURL=useAppearance.js.map